/* eslint no-magic-numbers: 0, no-mixed-operators : 0 */
import windProbs from './wind/wind-probs.js';
import windDirections from './wind/wind-directions';

const wind = [
	[0.6, 0.55, 0.6, 0.55, 0.6],
	[1, 0.7, 0.6, 0.6, 0.6],
	[1.4, 0.7, 0.55, 0.5, 0.4],
	[2, 0.75, 0.5, 0.2, 0.1]
];

/**
 * Calculate the wind speed using the lookup table
 * @param {number} dist The distance the cell is from the wind direction. 0-4. 0 is in in front of the direction while 4 is behind.
 * @param {number} strength The strength of the wind speed. 1-4
 * @param {Array<number[]>} windInfluence Alternative wind influence data to override default values. The first array should hold the different wind strength arrays starting with no wind. Each wind strength array should hold the wind influence for different distances starting with the direction of the wind and ending with directly behind the wind direction.
 * @return {number} A float indicating the wind influence modifier for a cell at the given distance
 **/
function calcWindInfluence(dist, strength, windInfluence = wind) {
	return windInfluence[strength - 1][dist];
}

/**
 * Get direction vectors to the immediate neighbours from the cell in the distance.
 * @param {number[]} direction A unit vector for the distance the wind is blowing. [0,0] is not a valid direction and will return weird results
 * @param {number} distance The distance to get neighbours from the direction. 0-4
 **/
function getNeighbourDirections(direction, distance) {
	if(distance === 0) {
		return [direction];
	}

	if(direction[1] === 0) {
		// Horizontal direction
		if(distance === 4) {

			const x = direction[0] - (direction[0] * 2);
			return [[x, 0]];
		}

		const x = direction[0] - (direction[0] * (distance - 1));
		return [[x, -1], [x, 1]];
	}

	if(direction[0] === 0) {
		// Vertical direction
		if(distance === 4) {

			const y = direction[1] - (direction[1] * 2);
			return [[0, y]];
		}

		const y = direction[1] - (direction[1] * (distance - 1));
		return [[-1, y], [1, y]];
	}

	// Diagonal direction
	if(distance === 4) {
		return [[direction[0] - (direction[0] * 2), direction[1] - (direction[1] * 2)]];
	}

	if(distance === 3) {
		return [
			[direction[0] - direction[0], direction[1] - direction[1] * 2],
			[direction[0] - direction[0] * 2, direction[1] - direction[1]],
		];
	}

	return [
		[direction[0], direction[1] - direction[1] * distance],
		[direction[0] - direction[0] * distance, direction[1]]
	];
}


/**
 * Calculate the new wind speed using the markov chain
 * @param {number} currentSpeed The current wind speed for the cell
 * @param {number} originalSpeed The original wind speed for the cell
 * @return {number} The new wind speed after evaluating the markov chain step
 **/
function calcWindChange(currentSpeed, originalSpeed) {
	const probs = windProbs[originalSpeed][currentSpeed];

	const changeChance = Math.random();
	let checkProb = 0;
	for(let i = 0; i < probs.length; i++) {
		checkProb += probs[i].prob;
		if(changeChance < checkProb) {
			return currentSpeed + probs[i].change;
		}
	}
	return currentSpeed;
}

const throwDistCalculation = {
	50: () => 0,
	60: () => 2,
	70: () => 3,
	85: (distance) => distance / 3,
	95: (distance) => distance,
	98: (distance) => distance * 2,
	99: (distance) => distance * 5,
	101: (distance) => distance * 10
};

/**
 * Calculate the ember throw distance for the given wind speed
 * @param {number} windSpeed The current wind speed. An integer in the range 1-4.
 * @return {number} A random distance to throw embers at the current wind speed
 **/
function calcThrowDistance(windSpeed) {
	if(windSpeed < 4) {
		return 0;
	}

	const throwChance = Math.random() * 200; // Increased from 100 to 200 to halve the chance of spotting
	const throwDistance = Math.round(Math.random() * 15);

	const calcKey = Object.keys(throwDistCalculation)
		.sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
		.find((key) => throwChance <= parseInt(key, 10));

	if (!calcKey || !throwDistCalculation[calcKey]) {
		return 0;
	}

	return Math.round(throwDistCalculation[calcKey](throwDistance));
}

export default calcWindInfluence;

export {
	calcWindInfluence,
	calcWindChange,
	getNeighbourDirections,
	windDirections,
	calcThrowDistance,
	wind as windInfluenceData
};
